import { useEffect, useRef, useState } from 'react';
import { WorkbookInstance } from '@fortune-sheet/react';
import { toast } from 'react-toastify';
import { fileBytesToFortuneSheets, downloadAsXlsx, downloadAsCsv } from './converters';
import { FortuneSheet } from './types';

interface UseSheetEditorDrawerLogicArgs {
    open: boolean;
    fileUrl: string;
    mimeType?: string;
    fileName: string;
}

const EMPTY_SHEET: FortuneSheet = { name: 'Sheet1', id: '0', order: 0, status: 1, celldata: [], row: 50, column: 26 };

/**
 * Owns the sheet-editor drawer's non-UI concerns: fetch the file, parse it into
 * Fortune-Sheet data, track loading/error, and expose download handlers. Keeps
 * SheetEditorDrawer focused on composition/JSX.
 */
export const useSheetEditorDrawerLogic = ({ open, fileUrl, mimeType, fileName }: UseSheetEditorDrawerLogicArgs) => {
    const [sheets, setSheets] = useState<FortuneSheet[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const workbookRef = useRef<WorkbookInstance>(null);

    useEffect(() => {
        if (!open || !fileUrl) return;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setSheets(null);

        (async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error('Failed to fetch file');
                const buffer = await response.arrayBuffer();
                const parsed = fileBytesToFortuneSheets(buffer, mimeType);
                setSheets(parsed.length ? parsed : [EMPTY_SHEET]);
                setLoading(false);
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [open, fileUrl, mimeType]);

    /** Latest sheet state: prefer the live workbook, fall back to parsed data. */
    const currentSheets = (): FortuneSheet[] => {
        const live = workbookRef.current?.getAllSheets();
        return live && live.length ? live : sheets ?? [];
    };

    const handleDownloadXlsx = () => {
        const data = currentSheets();
        if (!data.length) return;
        downloadAsXlsx(data, fileName);
        toast.success('Downloaded as .xlsx');
    };

    const handleDownloadCsv = () => {
        const data = currentSheets();
        if (!data.length) return;
        const active = data.find((s) => s.status === 1) ?? data[0];
        downloadAsCsv(active, fileName);
        toast.success('Downloaded active sheet as .csv');
    };

    return { sheets, loading, error, workbookRef, handleDownloadXlsx, handleDownloadCsv };
};
